import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { useCallback, useMemo } from 'react';
import { type FieldPath, useFormContext, useFormState } from 'react-hook-form';
import type {
  Referral,
  ReferralTypeValue,
} from '@/common/hooks/referrals/types';
import {
  type UpdateReferralInput,
  useUpdateReferral,
} from '@/common/hooks/referrals/useUpdateReferral';
import {
  useResolveTeamIdentityId,
  useResolveUserIdentityId,
} from '@/common/hooks/useResolveIdentityId';
import {
  ContactChangeAction,
  type ContactChangeDetail,
} from '@/components/ContactChangeDialog/types';
import { useAgentProfileDiff } from '$agent-profiles';

type AgentSyncData = {
  updateInput: UpdateReferralInput;
  fieldName: 'referringAgent' | 'transactingAgent';
};

type AgentPartial = Partial<NonNullable<Referral['referringAgent']>>;

const AGENT_FIELDS: FieldPath<Referral>[] = [
  'referringAgent',
  'transactingAgent',
];

function isDirty(dirtyField: unknown): boolean {
  if (typeof dirtyField === 'object' && dirtyField !== null) {
    return Object.values(dirtyField).some(isDirty);
  }
  return Boolean(dirtyField);
}

function useAgentChangedFields(agent: AgentPartial) {
  const { resolvedId: userIdentityId } = useResolveUserIdentityId(
    agent?.userId,
  );
  const { resolvedId: teamIdentityId } = useResolveTeamIdentityId(
    agent?.teamId,
  );
  return useAgentProfileDiff(agent, userIdentityId, teamIdentityId);
}

interface UseReferralAgentSyncParams {
  referringAgent: AgentPartial;
  transactingAgent: AgentPartial;
  referralType: ReferralTypeValue;
  referralId: string;
}

export function useReferralAgentSync({
  referringAgent,
  transactingAgent,
  referralType,
  referralId,
}: UseReferralAgentSyncParams) {
  const { setValue, control } = useFormContext<Referral>();
  const { dirtyFields } = useFormState({ control, name: AGENT_FIELDS });
  const updateReferral = useUpdateReferral(referralId);

  const referringAgentDirty = isDirty(dirtyFields.referringAgent);
  const transactingAgentDirty = isDirty(dirtyFields.transactingAgent);

  const referringAgentFields = useAgentChangedFields(referringAgent);
  const transactingAgentFields = useAgentChangedFields(transactingAgent);

  const changedRecords: ContactChangeDetail<AgentSyncData>[] = useMemo(() => {
    const result: ContactChangeDetail<AgentSyncData>[] = [];

    // Only diff a fully-identified, pristine agent. A dirty field holds an
    // unsaved local edit whose sparse values would diff against the identity
    // profile; the save's reset restores the pristine, server-enriched value.
    if (
      referringAgentFields.length > 0 &&
      !referringAgentDirty &&
      referringAgent.id &&
      referringAgent.firstName &&
      referringAgent.userId &&
      referringAgent.teamId
    ) {
      result.push({
        id: 'referringAgent',
        type: 'agent',
        label: 'Referring Agent',
        action: ContactChangeAction.Edited,
        fields: referringAgentFields,
        updateInput: {
          referringAgent: {
            userId: referringAgent.userId,
            teamId: referringAgent.teamId,
            type: referralType === 'sending' ? 'agent' : 'counterpartyAgent',
          },
        },
        fieldName: 'referringAgent',
      });
    }

    if (
      transactingAgentFields.length > 0 &&
      !transactingAgentDirty &&
      transactingAgent.id &&
      transactingAgent.firstName &&
      transactingAgent.userId &&
      transactingAgent.teamId
    ) {
      result.push({
        id: 'transactingAgent',
        type: 'agent',
        label: 'Transacting Agent',
        action: ContactChangeAction.Edited,
        fields: transactingAgentFields,
        updateInput: {
          transactingAgent: {
            userId: transactingAgent.userId,
            teamId: transactingAgent.teamId,
            type: referralType === 'receiving' ? 'agent' : 'counterpartyAgent',
          },
        },
        fieldName: 'transactingAgent',
      });
    }

    return result;
  }, [
    referringAgent,
    transactingAgent,
    referralType,
    referringAgentFields,
    transactingAgentFields,
    referringAgentDirty,
    transactingAgentDirty,
  ]);

  const syncContacts = useCallback(
    async (selectedContacts: ContactChangeDetail<AgentSyncData>[]) => {
      const input: UpdateReferralInput = {};
      for (const record of selectedContacts) {
        Object.assign(input, record.updateInput);
      }

      try {
        await updateReferral(input);
      } catch (error) {
        const errorMessage = `Failed to sync ${selectedContacts.length === 1 ? 'contact' : 'contacts'}`;
        toast('error', errorMessage);
        datadogRum.addError(error, {
          message: errorMessage,
          contactsToSync: selectedContacts,
        });
        throw error;
      }

      for (const record of selectedContacts) {
        for (const field of record.fields) {
          setValue(
            `${record.fieldName}.${field.propName}` as FieldPath<Referral>,
            field.newValue,
          );
        }
      }

      toast(
        'success',
        `${selectedContacts.length === 1 ? 'Contact' : 'Contacts'} synced`,
      );
    },
    [updateReferral, setValue],
  );

  return { changedRecords, syncContacts };
}
